import React from 'react';
import {
  Box,
  Container,
  Grid,
  Typography,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  IconButton,
  Button,
} from '@mui/material';
import { Line, Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js';
import { KeyboardArrowDown, MoreVert, TrendingUp } from '@mui/icons-material';

import { dashboardStyles, cardStyles, chartContainerStyles } from '../styles/components/Dashboard.styles';
import { statCardStyles } from '../styles/components/StatCard.styles';
import { exploreCardStyles } from '../styles/components/ExploreCard.styles';
import { tableStyles } from '../styles/components/Table.styles';
import { chartCommonOptions, sparklineOptions } from '../styles/components/Chart.styles';

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

const SparklineChart = ({ data, color, type = 'line', id }) => {
  const chartData = {
    labels: new Array(data.length).fill(''),
    datasets: [
      {
        data,
        borderColor: color,
        borderWidth: 2,
        fill: true,
        backgroundColor: type === 'area' ? `${color}08` : 'transparent',
        tension: 0.5,
        pointRadius: 0,
      },
    ],
  };

  return (
    <Box sx={{ height: 36, width: '100%' }}>
      <Line data={chartData} options={sparklineOptions} id={id} />
    </Box>
  );
};

const StatCard = ({ title, value, change, period, chartData, chartColor }) => (
  <Card sx={cardStyles.root}>
    <CardContent sx={cardStyles.content}>
      <Typography variant="subtitle2" sx={statCardStyles.title}>
        {title}
      </Typography>
      <Box sx={statCardStyles.valueContainer}>
        <Typography variant="h4" sx={statCardStyles.value}>
          {value}
        </Typography>
        <Typography variant="subtitle2" sx={statCardStyles.change(change.startsWith('+'))}>
          {change}
        </Typography>
      </Box>
      <Typography variant="body2" sx={statCardStyles.period}>
        {period}
      </Typography>
      <SparklineChart data={chartData} color={chartColor} />
    </CardContent>
  </Card>
);

const ExploreCard = () => (
  <Card sx={cardStyles.root}>
    <CardContent sx={exploreCardStyles.content}>
      <Box sx={exploreCardStyles.header}>
        <TrendingUp sx={exploreCardStyles.icon} />
        <Typography variant="subtitle2" sx={exploreCardStyles.title}>
          Explore your data
        </Typography>
      </Box>
      <Typography variant="body2" sx={exploreCardStyles.description}>
        Uncover performance and visitor insights with our data wizardry.
      </Typography>
      <Button variant="outlined" size="small" sx={exploreCardStyles.button}>
        Get insights
      </Button>
    </CardContent>
  </Card>
);

const Dashboard = () => {
  // Sessions area chart data
  const sessionsData = {
    labels: ['Apr 5', 'Apr 10', 'Apr 15', 'Apr 20', 'Apr 25', 'Apr 30'],
    datasets: [
      {
        label: 'Current period',
        data: [2000, 4000, 6000, 8000, 10000, 13277],
        fill: true,
        borderColor: 'rgb(0, 184, 217)',
        backgroundColor: (context) => {
          const ctx = context.chart.ctx;
          const gradient = ctx.createLinearGradient(0, 0, 0, 400);
          gradient.addColorStop(0, 'rgba(0, 184, 217, 0.32)');
          gradient.addColorStop(1, 'rgba(0, 184, 217, 0)');
          return gradient;
        },
        tension: 0.4,
        borderWidth: 2,
      },
      {
        label: 'Previous period',
        data: [1000, 2000, 3000, 4000, 5000, 6000],
        fill: true,
        borderColor: 'rgba(145, 158, 171, 0.24)',
        backgroundColor: 'transparent',
        tension: 0.4,
        borderWidth: 2,
      },
    ],
  };

  // Page views bar chart data
  const pageViewsData = {
    labels: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul'],
    datasets: [
      {
        label: 'Downloads',
        data: [4000, 4500, 3500, 4000, 5000, 3000, 2500],
        backgroundColor: 'rgb(0, 184, 217)',
        barThickness: 6,
        borderRadius: 3,
        stack: 'stack0',
        borderSkipped: false,
      },
      {
        label: 'Page Views',
        data: [5000, 5500, 5000, 7000, 7000, 5000, 5000],
        backgroundColor: 'rgba(0, 184, 217, 0.24)',
        barThickness: 6,
        borderRadius: 3,
        stack: 'stack0',
        borderSkipped: false,
      },
    ],
  };

  return (
    <Box sx={dashboardStyles.root}>
      <Container maxWidth={false} disableGutters>
        <Typography variant="h4" sx={dashboardStyles.title}>
          Overview
        </Typography>
        <Grid container spacing={3}>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="Users"
              value="14k"
              change="+25%"
              period="Last 30 days"
              chartData={[65, 67, 66, 68, 67, 69, 68, 70, 69, 71, 70, 72, 71, 73, 72, 74, 73, 75, 74, 76]}
              chartColor="rgb(84, 214, 44)"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="Conversions"
              value="325"
              change="-25%"
              period="Last 30 days"
              chartData={[76, 74, 73, 72, 71, 70, 69, 68, 67, 66, 65, 64, 63, 62, 61, 60, 59, 58, 57, 56]}
              chartColor="rgb(255, 72, 66)"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="Event count"
              value="200k"
              change="+5%"
              period="Last 30 days"
              chartData={[68, 69, 68, 69, 68, 69, 68, 69, 68, 69, 68, 69, 68, 69, 68, 69, 68, 69, 68, 69]}
              chartColor="rgb(0, 184, 217)"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <ExploreCard />
          </Grid>
          <Grid item xs={12} md={8}>
            <Card sx={cardStyles.root}>
              <CardContent sx={cardStyles.content}>
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle2" sx={chartContainerStyles.title}>
                    Sessions
                  </Typography>
                  <Typography variant="body2" sx={chartContainerStyles.subtitle}>
                    13,277 <Box component="span" sx={{ color: 'rgb(84, 214, 44)' }}>
                      (+35%)
                    </Box> Sessions per day for the last 30 days
                  </Typography>
                </Box>
                <Box sx={{ height: 375 }}>
                  <Line data={sessionsData} options={chartCommonOptions} id="sessions-chart" />
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={4}>
            <Card sx={cardStyles.root}>
              <CardContent sx={cardStyles.content}>
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle2" sx={chartContainerStyles.title}>
                    Page views and downloads
                  </Typography>
                  <Typography variant="body2" sx={chartContainerStyles.subtitle}>
                    1.3M <Box component="span" sx={{ color: 'rgb(255, 72, 66)' }}>
                      (-8%)
                    </Box> Page views and downloads for the last 6 months
                  </Typography>
                </Box>
                <Box sx={{ height: 375 }}>
                  <Bar data={pageViewsData} options={chartCommonOptions} id="pageviews-chart" />
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12}>
            <Card sx={cardStyles.root}>
              <CardContent sx={cardStyles.content}>
                <Typography variant="subtitle2" sx={chartContainerStyles.title}>
                  Details
                </Typography>
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell padding="checkbox">
                          <Checkbox sx={tableStyles.checkbox} />
                        </TableCell>
                        <TableCell sx={tableStyles.cell}>Page Title</TableCell>
                        <TableCell sx={tableStyles.cell}>Status</TableCell>
                        <TableCell sx={tableStyles.cell}>Users</TableCell>
                        <TableCell sx={tableStyles.cell}>Event Count</TableCell>
                        <TableCell sx={tableStyles.cell}>Views per User</TableCell>
                        <TableCell sx={tableStyles.cell}>Average Time</TableCell>
                        <TableCell sx={tableStyles.cell}>Daily Conversions</TableCell>
                        <TableCell sx={tableStyles.cell}></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {[
                        {
                          id: 1,
                          title: 'Homepage Overview',
                          status: 'Online',
                          users: '212,423',
                          eventCount: '8,345',
                          viewsPerUser: '18.5',
                          avgTime: '2m 15s',
                          conversions: Array(30).fill(1).map(() => Math.random() * 100),
                        },
                        {
                          id: 2,
                          title: 'Product Details - Gadgets',
                          status: 'Online',
                          users: '172,240',
                          eventCount: '5,653',
                          viewsPerUser: '9.7',
                          avgTime: '2m 30s',
                          conversions: Array(30).fill(1).map(() => Math.random() * 60),
                        },
                      ].map((row) => (
                        <TableRow key={row.id} hover sx={tableStyles.row}>
                          <TableCell padding="checkbox">
                            <Checkbox sx={tableStyles.checkbox} />
                          </TableCell>
                          <TableCell sx={tableStyles.cell}>{row.title}</TableCell>
                          <TableCell>
                            <Box sx={tableStyles.status}>{row.status}</Box>
                          </TableCell>
                          <TableCell sx={tableStyles.cell}>{row.users}</TableCell>
                          <TableCell sx={tableStyles.cell}>{row.eventCount}</TableCell>
                          <TableCell sx={tableStyles.cell}>{row.viewsPerUser}</TableCell>
                          <TableCell sx={tableStyles.cell}>{row.avgTime}</TableCell>
                          <TableCell sx={tableStyles.sparklineCell}>
                            <SparklineChart
                              data={row.conversions}
                              color="rgb(0, 184, 217)"
                              type="area"
                              id={`sparkline-${row.id}`}
                            />
                          </TableCell>
                          <TableCell align="right">
                            <IconButton size="small">
                              <KeyboardArrowDown />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default Dashboard; 